import React, { useState, useMemo, useEffect } from "react";
import { Modal, Button, Form, Accordion } from "react-bootstrap";
import "./OpalApprovableMissedCallModal.css";
import TauriImage from "../../TauriImage/TauriImage";
import appURL from '../../../appURL.json';
import TauriLoader2 from "../../TauriLoader2/TauriLoader2";
import { addRecord, getAll, getNextId, updateRecord } from "../../../localDB";
import { transformForAcknowledgementData, transformMissedCallRequests } from "../../OpalCoverageLayout/CoverageFactoryData/CoverageFactoryData";
import OpalAlertModal from "../../OpalAlertModal/OpalAlertModal";
import { MissedCallRequestStatus } from "../../../visualizations/visualizations";
import OpalDataTable from "../../OpalDataTable/OpalDataTable";

const OpalApprovableMissedCallModal = ({ show, onHide }) => {
  const [loading, setIsLoading] = useState(false);
  const [alertLoading, setAlertLoading] = useState(false);
  const [data, setData] = useState([]);
  const [showMessage, setShowMessage] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);

  const cycle_period = localStorage.getItem("c_cd");

  const columns = [
    { key: "Employee.Name", label: "Employee" },
    { key: "RequestDate", label: "Request Date" },
    { key: "Reason", label: "Reason" },
    { key: "Status", label: "Status" }
  ];

  const actions = [
    {
      label: "Approve",
      onClick: row => {
        setAlertMessage({
          title: "Confirm",
          bodyText: "Are you sure you want to approve this request?",
          onConfirm: () => {
            ApproveMissedCall(row);
          },
          confirmText: "Yes",
          cancelText: "No",
          type: "confirm"
        });

        setShowMessage(true);
      }
    },
    {
      label: "Reject",
      onClick: row => {
        setAlertMessage({
          title: "Confirm",
          bodyText: "Are you sure you want to reject this request?",
          onConfirm: () => {
            RejectMissedCall(row);
          },
          confirmText: "Yes",
          cancelText: "No",
          type: "confirm"
        });

        setShowMessage(true);
      }
    }
  ];

  useEffect(() => {
    fetchOnlineData();
  }, [show])

  const fetchOnlineData = async () => {
    try {
      setIsLoading(true);
      const isOnline = JSON.parse(localStorage.getItem("isOnline"));
      const hasInternet = navigator.onLine;

      if (isOnline && hasInternet) {
        const token = localStorage.getItem("token");
        const devId = localStorage.getItem("devId");

        const cycleID = localStorage.getItem("c_id");
        const response = await fetch(`${appURL.API_MissedCallRequest}/approvables/${cycleID}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            "X-ETR-Device-ID": devId
          },
        });

        if (!response.ok) {
          return;
        }

        const reasons = await getAll("MissedCallReasons");
        const res = await response.json();
        const request = await transformMissedCallRequests(res?.missedCallRequest || [], reasons);

        setData(request);
      }
    } catch (error) {
      return;
    } finally {
      setIsLoading(false);
    }
  }

  const ApproveMissedCall = async (row) => {
    try {
      setAlertLoading(true);
      const isOnline = JSON.parse(localStorage.getItem("isOnline"));
      const hasInternet = navigator.onLine;

      if (isOnline && hasInternet) {
        const token = localStorage.getItem("token");
        const devId = localStorage.getItem("devId");

        const request_id = Number(row?.ID);
        const response = await fetch(`${appURL.API_MissedCallRequest}/approve/${request_id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            "X-ETR-Device-ID": devId
          },
        });

        if (!response.ok) {
          const error = await response.json();
          if (!error) return;
          const message = {
            title: error?.category || "Error",
            bodyText: error?.description || "An unexpected error occurred. Please try again later.",
            modalType: "message"
          };

          setAlertMessage(message);
          setShowMessage(true);
          return;
        };

        setShowMessage(false);
        window.dispatchEvent(new Event("refreshMissedCallRequest"));
        await fetchOnlineData();
      }
    } catch (error) {
      return;
    } finally {
      setAlertLoading(false);
    }
  }

  const RejectMissedCall = async (row) => {
    try {
      setAlertLoading(true);
      const isOnline = JSON.parse(localStorage.getItem("isOnline"));
      const hasInternet = navigator.onLine;

      if (isOnline && hasInternet) {
        const token = localStorage.getItem("token");
        const devId = localStorage.getItem("devId");

        const request_id = Number(row?.ID);
        const response = await fetch(`${appURL.API_MissedCallRequest}/reject/${request_id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            "X-ETR-Device-ID": devId
          },
        });

        if (!response.ok) {
          const error = await response.json();
          if (!error) return;
          const message = {
            title: error?.category || "Error",
            bodyText: error?.description || "An unexpected error occurred. Please try again later.",
            modalType: "message"
          };

          setAlertMessage(message);
          setShowMessage(true);
          return;
        };

        setShowMessage(false);
        window.dispatchEvent(new Event("refreshMissedCallRequest"));
        await fetchOnlineData();
      }
    } catch (error) {
      return;
    } finally {
      setAlertLoading(false);
    }
  }

  return (
    <Modal
      className="opal-missed-call-request-modal"
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header>
        <div>{`Team Missed Call Requests - ${cycle_period}`}</div>
        <TauriImage localImage={"/Images/material-symbols--close.svg"} className="modal-close-icon" color={"#FFFFFF"} onClick={onHide} />
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <TauriLoader2 width="2.5rem" height="2.5rem" borderwidth="0.35rem" color="#1D5D9B" />
        ) : (
          <OpalDataTable
            loading={loading}
            data={data}
            columns={columns}
            actions={actions}
          />
        )}

      </Modal.Body>

      <Modal.Footer>
        <Button className="button-close" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
      <OpalAlertModal
        show={showMessage}
        onHide={() => setShowMessage(false)}
        title={alertMessage?.title}
        bodyText={alertMessage?.bodyText}
        onConfirm={alertMessage?.onConfirm}
        confirmButtonText={alertMessage?.confirmText}
        cancelButtonText={alertMessage?.cancelText}
        loading={alertLoading}
        modalType={alertMessage?.type ?? "message"}
      />

    </Modal>
  );
};

export default OpalApprovableMissedCallModal;